import React, { useEffect, useState } from 'react';
import { Card, List, Space, Typography, Tag, Descriptions, Modal } from 'antd';
import { CheckSquare, Eye } from 'lucide-react';
import { useCrewStore } from '../stores/useCrewStore';
import { apiService } from '../services/api';
import type { Task } from '../types';

const { Title, Text } = Typography;

const TasksPage: React.FC = () => {
  const { tasks, agents, setTasks } = useCrewStore();
  const [loading, setLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    setLoading(true);
    try {
      const data = await apiService.getTasks();
      setTasks(data);
    } catch (error) {
      console.error('Failed to load tasks:', error);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const getAgentName = (agentId?: string) => {
    if (!agentId) return 'Unassigned';
    const agent = agents.find(a => a.id === agentId);
    return agent?.name || 'Unknown';
  };

  const handleViewDetails = (task: Task) => {
    setSelectedTask(task);
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <CheckSquare className="inline-block mr-2" size={24} /> Tasks
        </Title>
        <Text type="secondary">View tasks currently in use by the system</Text>
      </div>

      {tasks.length === 0 && !loading ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <CheckSquare size={64} className="text-gray-300" />
            <Title level={4} style={{ marginTop: 16 }}>No tasks in use</Title>
            <Text type="secondary">There are currently no tasks being used by the system</Text>
          </div>
        </Card>
      ) : (
        <List
          grid={{ gutter: 16, xs: 1, sm: 1, md: 2, lg: 2, xl: 3, xxl: 3 }}
          dataSource={tasks}
          loading={loading}
          renderItem={(task) => (
            <List.Item>
              <Card
                hoverable
                onClick={() => handleViewDetails(task)}
                style={{ cursor: 'pointer' }}
              >
                <Card.Meta
                  title={task.name}
                  description={
                    <Text ellipsis style={{ maxWidth: '100%' }}>
                      {task.description}
                    </Text>
                  }
                />
                <div style={{ marginTop: 16 }}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <div>
                      <Text type="secondary">Agent: </Text>
                      <Tag color="blue">{getAgentName(task.agent_id)}</Tag>
                    </div>
                    <Space size="small" wrap>
                      {task.async_execution && (
                        <Tag color="purple">Async</Tag>
                      )}
                      {task.tools && task.tools.length > 0 && (
                        <Tag>{task.tools.length} tool(s)</Tag>
                      )}
                      {task.context && task.context.length > 0 && (
                        <Tag color="orange">{task.context.length} context(s)</Tag>
                      )}
                    </Space>
                  </Space>
                </div>
                <div style={{ marginTop: 12, textAlign: 'right' }}>
                  <Text type="secondary">
                    <Eye size={14} className="inline-block mr-1" /> Click to view details
                  </Text>
                </div>
              </Card>
            </List.Item>
          )}
        />
      )}

      <Modal
        title="Task Details"
        open={!!selectedTask}
        onCancel={() => setSelectedTask(null)}
        footer={null}
        width={700}
      >
        {selectedTask && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="Name">{selectedTask.name}</Descriptions.Item>
            <Descriptions.Item label="Description">{selectedTask.description}</Descriptions.Item>
            <Descriptions.Item label="Expected Output">{selectedTask.expected_output}</Descriptions.Item>
            <Descriptions.Item label="Assigned Agent">
              <Tag color="blue">{getAgentName(selectedTask.agent_id)}</Tag>
            </Descriptions.Item>
            {selectedTask.context && selectedTask.context.length > 0 && (
              <Descriptions.Item label="Context">
                <Space size={[0, 8]} wrap>
                  {selectedTask.context.map((ctx, idx) => (
                    <Tag key={idx}>{ctx}</Tag>
                  ))}
                </Space>
              </Descriptions.Item>
            )}
            {selectedTask.tools && selectedTask.tools.length > 0 && (
              <Descriptions.Item label="Tools">
                <Space size={[0, 8]} wrap>
                  {selectedTask.tools.map((toolId, idx) => (
                    <Tag key={idx}>{toolId}</Tag>
                  ))}
                </Space>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Async Execution">
              <Tag color={selectedTask.async_execution ? 'purple' : 'default'}>
                {selectedTask.async_execution ? 'Yes' : 'No'}
              </Tag>
            </Descriptions.Item>
            {selectedTask.created_at && (
              <Descriptions.Item label="Created At">
                {new Date(selectedTask.created_at).toLocaleString()}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default TasksPage;
